import { DollarSign, ShoppingCart, CreditCard, Banknote, PieChart, BarChart3, ArrowRight, TrendingUp, Info } from 'lucide-react'
import { SkeletonPage } from '@/components/ui/Skeleton'
import {
  PieChart as RechartsPie, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts'
import { useAuth } from '@/contexts/AuthContext'
import { useEventData } from '@/contexts/EventDataContext'

const PURCHASE_COLORS = ['#0070ab', '#00c7ca', '#ed00be', '#f59e0b']
const ALLOCATION_COLORS = ['#0070ab', '#00c7ca', '#ed00be', '#f59e0b', '#94a3b8']
const PAYMENT_COLORS = ['#22c55e', '#6366f1']

function fmt(cents: number): string {
  const dollars = cents / 100
  if (dollars >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(1)}M`
  if (dollars >= 1_000) return `$${(dollars / 1_000).toFixed(1)}K`
  return `$${dollars.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
}

function fmtPct(value: number): string {
  return `${value.toFixed(1)}%`
}

function SectionHeader({ icon: Icon, title, subtitle }: { icon: typeof DollarSign; title: string; subtitle?: string }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <Icon className="h-4 w-4 text-butler-600" />
      <h2 className="font-display font-semibold text-base text-ink-dark">{title}</h2>
      {subtitle && <span className="text-xs text-ink-soft">— {subtitle}</span>}
    </div>
  )
}

function KpiCard({ label, value, sub, icon }: { label: string; value: string; sub?: string; icon?: React.ReactNode }) {
  return (
    <div className="mm-card-v3 p-4">
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="text-xs text-ink-soft">{label}</span>
      </div>
      <div className="font-display font-bold text-xl text-ink-dark">{value}</div>
      {sub && <div className="text-xs text-ink-soft mt-1">{sub}</div>}
    </div>
  )
}

function CustomTooltip({ active, payload }: { active?: boolean; payload?: Array<{ name: string; value: number; payload: { label: string; cents: number; pct: number } }> }) {
  if (!active || !payload?.[0]) return null
  const d = payload[0].payload
  return (
    <div className="bg-white shadow-lg rounded-lg border border-slate-200 p-3 text-xs">
      <div className="font-semibold text-ink-dark">{d.label}</div>
      <div className="text-ink-soft mt-1">{fmt(d.cents)} ({fmtPct(d.pct)})</div>
    </div>
  )
}

export function RevenueBreakdownPage() {
  const { currentEventId } = useAuth()
  const { revenueBreakdown: data, loading } = useEventData()

  if (currentEventId == null) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="rounded-lg border border-dashed border-slate-300 p-12 text-center">
          <h2 className="font-display font-bold text-lg text-ink-dark mb-2">Revenue Breakdown</h2>
          <p className="text-sm text-ink-soft">Select an event to see revenue composition.</p>
        </div>
      </div>
    )
  }

  if (loading && !data) {
    return <SkeletonPage />
  }

  if (!data) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="mm-card-v3 p-8 text-center">
          <Info className="h-8 w-8 text-ink-soft mx-auto mb-3" />
          <h2 className="font-display font-semibold text-ink-dark mb-1">No Revenue Data</h2>
          <p className="text-sm text-ink-soft">Revenue breakdown data requires ETL backfill with purchase type extraction.</p>
        </div>
      </div>
    )
  }

  const totalGross = data.revenue.total_gross_cents
  const purchaseData = data.purchase_breakdown
    .filter(p => p.cents > 0)
    .map(p => ({
      ...p,
      pct: totalGross > 0 ? (p.cents / totalGross) * 100 : 0,
    }))

  const paymentData = data.payment_method
    .filter(p => p.cents > 0)
    .map(p => ({
      ...p,
      pct: totalGross > 0 ? (p.cents / totalGross) * 100 : 0,
    }))

  const allocationData = data.credit_allocation.map(a => ({
    ...a,
    pct: data.credit_utilization.total_loaded_cents > 0
      ? (a.cents / data.credit_utilization.total_loaded_cents) * 100
      : 0,
  }))

  const hasBreakdownData = purchaseData.length > 0

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Hero KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Total Revenue"
          value={fmt(totalGross)}
          icon={<DollarSign className="h-4 w-4 text-butler-600" />}
          sub={data.revenue.settlement_gross_cents != null
            ? `Settlement: ${fmt(data.revenue.settlement_gross_cents)}`
            : undefined}
        />
        <KpiCard
          label="Total Orders"
          value={data.total_orders.toLocaleString()}
          icon={<ShoppingCart className="h-4 w-4 text-butler-600" />}
        />
        <KpiCard
          label="Breakage"
          value={fmt(data.breakage.total_cents)}
          icon={<TrendingUp className="h-4 w-4 text-green-600" />}
          sub={`${data.breakage.bands_with_breakage.toLocaleString()} bands`}
        />
        <KpiCard
          label="Credit Utilization"
          value={fmtPct(data.credit_utilization.utilization_rate)}
          icon={<CreditCard className="h-4 w-4 text-butler-600" />}
          sub={`${fmt(data.credit_utilization.total_spent_cents)} of ${fmt(data.credit_utilization.total_loaded_cents)}`}
        />
      </div>

      {/* Purchase Type Breakdown + Payment Method */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Purchase Type Donut */}
        <section className="mm-card-v3 p-5">
          <SectionHeader icon={PieChart} title="Revenue by Purchase Type" />
          {!hasBreakdownData ? (
            <div className="py-8 text-center text-xs text-ink-soft">
              Purchase type data not yet populated. Run ETL backfill.
            </div>
          ) : (
            <div className="flex items-center gap-6">
              <div className="w-48 h-48 shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPie>
                    <Pie
                      data={purchaseData}
                      dataKey="cents"
                      nameKey="label"
                      cx="50%"
                      cy="50%"
                      innerRadius={45}
                      outerRadius={75}
                      paddingAngle={2}
                    >
                      {purchaseData.map((_, i) => (
                        <Cell key={i} fill={PURCHASE_COLORS[i % PURCHASE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </RechartsPie>
                </ResponsiveContainer>
              </div>
              <div className="space-y-3 flex-1 min-w-0">
                {purchaseData.map((p, i) => (
                  <div key={p.type} className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: PURCHASE_COLORS[i % PURCHASE_COLORS.length] }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-sm text-ink-dark truncate">{p.label}</span>
                        <span className="mm-data text-sm font-semibold text-ink-dark shrink-0">{fmt(p.cents)}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${p.pct}%`, backgroundColor: PURCHASE_COLORS[i % PURCHASE_COLORS.length] }} />
                        </div>
                        <span className="text-xs text-ink-soft w-10 text-right">{fmtPct(p.pct)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Payment Method */}
        <section className="mm-card-v3 p-5">
          <SectionHeader icon={Banknote} title="Payment Method" />
          {paymentData.length === 0 ? (
            <div className="py-8 text-center text-xs text-ink-soft">No payment data.</div>
          ) : (
            <>
              <div className="flex items-center gap-6 mb-4">
                <div className="w-48 h-48 shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPie>
                      <Pie
                        data={paymentData}
                        dataKey="cents"
                        nameKey="label"
                        cx="50%"
                        cy="50%"
                        innerRadius={45}
                        outerRadius={75}
                        paddingAngle={2}
                      >
                        {paymentData.map((_, i) => (
                          <Cell key={i} fill={PAYMENT_COLORS[i % PAYMENT_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </RechartsPie>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-3 flex-1">
                  {paymentData.map((p, i) => (
                    <div key={p.type} className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: PAYMENT_COLORS[i % PAYMENT_COLORS.length] }} />
                      <span className="text-sm text-ink-dark flex-1">{p.label}</span>
                      <span className="mm-data text-sm font-semibold text-ink-dark">{fmt(p.cents)}</span>
                      <span className="text-xs text-ink-soft w-10 text-right">{fmtPct(p.pct)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </section>
      </div>

      {/* Credit Allocation — where credits end up */}
      <section className="mm-card-v3 p-5">
        <SectionHeader icon={BarChart3} title="Credit Allocation" subtitle="Where purchased credits end up" />
        {allocationData.length === 0 ? (
          <div className="py-8 text-center text-xs text-ink-soft">No credit allocation data.</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={allocationData} layout="vertical" margin={{ left: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tickFormatter={(v: number) => fmt(v)} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="label" tick={{ fontSize: 12 }} width={60} />
                  <Tooltip formatter={(v: number) => fmt(v)} />
                  <Bar dataKey="cents" name="Credits" radius={[0, 4, 4, 0]}>
                    {allocationData.map((_, i) => (
                      <Cell key={i} fill={ALLOCATION_COLORS[i % ALLOCATION_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-4">
              {allocationData.map((a, i) => (
                <div key={a.type}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: ALLOCATION_COLORS[i % ALLOCATION_COLORS.length] }} />
                      <span className="text-sm font-medium text-ink-dark">{a.label}</span>
                    </div>
                    <span className="mm-data text-sm font-bold text-ink-dark">{fmt(a.cents)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${a.pct}%`, backgroundColor: ALLOCATION_COLORS[i % ALLOCATION_COLORS.length] }}
                      />
                    </div>
                    <span className="text-xs text-ink-soft w-16 text-right">
                      {fmtPct(a.pct)} · {a.unique_bands?.toLocaleString() ?? '—'} bands
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Revenue Waterfall Summary */}
      <section className="mm-card-v3 p-5">
        <SectionHeader icon={ArrowRight} title="Revenue Flow" subtitle="From purchase to credit consumption" />
        <div className="flex items-center gap-4 py-4 overflow-x-auto">
          <WaterfallStep label="Credits Purchased" value={fmt(data.credit_utilization.total_loaded_cents)} color="#0070ab" />
          <ArrowRight className="h-5 w-5 text-ink-soft shrink-0" />
          <WaterfallStep label="Credits Spent" value={fmt(data.credit_utilization.total_spent_cents)} color="#00c7ca" />
          <ArrowRight className="h-5 w-5 text-ink-soft shrink-0" />
          <WaterfallStep label="Breakage (Profit)" value={fmt(data.breakage.total_cents)} color="#22c55e" highlight />
        </div>
        <div className="mt-2 text-xs text-ink-soft">
          {fmtPct(data.credit_utilization.utilization_rate)} of loaded credits were spent.
          {' '}{fmtPct(100 - data.credit_utilization.utilization_rate)} remained as breakage (pure profit).
        </div>
      </section>
    </div>
  )
}

function WaterfallStep({ label, value, color, highlight }: { label: string; value: string; color: string; highlight?: boolean }) {
  return (
    <div className={`flex-1 min-w-[120px] rounded-lg border-2 p-4 text-center ${highlight ? 'border-green-200 bg-green-50' : 'border-slate-200'}`}>
      <div className="text-xs text-ink-soft mb-1">{label}</div>
      <div className="font-display font-bold text-lg" style={{ color }}>{value}</div>
    </div>
  )
}
